import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createOfflineApplicationThunk,
  getLegacyApplicationListThunk,
} from "./redux/thunk/dashboardThunk";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@store/store";
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  InputBase,
  Radio,
  RadioGroup,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { toast } from "react-toastify";
import AlertDialogBox from "@modules/stepper/components/AlertDialogBox";

interface LegacyDrawerProps {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}

type LegacyOnlineResult = {
  id: string;
  application_type: string;
  application_code: string;
};

const LegacyDrawer: React.FC<LegacyDrawerProps> = ({
  dialogOpen,
  setDialogOpen,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { profile } = useSelector((state: RootState) => state.profile);

  const organization = profile?.organizations?.[0];

  const hasOrganization = Boolean(
    organization?.gst_number &&
    organization?.business_name &&
    organization?.registration_date,
  );

  const [filteredOnlineResults, setFilteredOnlineResults] = useState<
    LegacyOnlineResult[]
  >([]);
  const [legacyApplicationType, setLegacyApplicationType] = useState("");
  const [legacyOption, setLegacyOption] = useState("");
  const [selectedLegacyApplication, setSelectedLegacyApplication] =
    useState<LegacyOnlineResult | null>(null);
  const [legacySearchValue, setLegacySearchValue] = useState("");
  const [offlineLegacySearchValue, setOfflineLegacySearchValue] = useState("");
  const [isOnlineSearching, setIsOnlineSearching] = useState(false);
  const [openOrganizationAlert, setOpenOrganizationAlert] = useState(false);

  const ONLINE_SEARCH_MIN_LENGTH = 3;

  const showOnlineDropdown =
    legacyOption === "online" &&
    !selectedLegacyApplication &&
    legacySearchValue.trim().length >= ONLINE_SEARCH_MIN_LENGTH;

  useEffect(() => {
    if (legacyOption !== "online" || selectedLegacyApplication) return;

    const query = legacySearchValue.trim();

    if (query.length < ONLINE_SEARCH_MIN_LENGTH) {
      setFilteredOnlineResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsOnlineSearching(true);

        const getLegacyList = await dispatch(
          getLegacyApplicationListThunk({ application_code: query }),
        ).unwrap();

        setFilteredOnlineResults(getLegacyList?.data || []);
      } catch {
        setFilteredOnlineResults([]);
      } finally {
        setIsOnlineSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [
    dispatch,
    legacyOption,
    legacySearchValue,
    selectedLegacyApplication,
    ONLINE_SEARCH_MIN_LENGTH,
  ]);

  return (
    <>
      {/* Legacy Application Side Tray */}
      <Drawer
        anchor="right"
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setLegacyOption("");
          setLegacySearchValue("");
          setFilteredOnlineResults([]);
          setLegacyApplicationType("");
          setSelectedLegacyApplication(null);
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 420 },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.5,
          }}
        >
          <Typography
            sx={{ fontSize: "16px", fontWeight: 700, color: "#212121" }}
          >
            For Application prior to February 2026
          </Typography>
          <IconButton
            size="small"
            onClick={() => {
              setDialogOpen(false);
              setLegacyOption("");
              setLegacySearchValue("");
              setFilteredOnlineResults([]);
              setLegacyApplicationType("");
              setSelectedLegacyApplication(null);
            }}
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>
        <Divider />

        {/* Content */}
        <Box sx={{ flex: 1, px: 3, py: 3 }}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: 700, color: "#212121", mb: 2 }}
          >
            Whether your old application processed online?
          </Typography>
          <RadioGroup
            value={legacyOption}
            onChange={(e) => {
              setLegacyOption(e.target.value);
              setLegacySearchValue("");
              setFilteredOnlineResults([]);
              setLegacyApplicationType("");
              setSelectedLegacyApplication(null);
            }}
          >
            <FormControlLabel
              value="online"
              control={
                <Radio
                  size="small"
                  sx={{
                    color: "#8BA6C7",
                    "&.Mui-checked": { color: "#3A1C73" },
                  }}
                />
              }
              label={
                <Typography
                  sx={{ fontSize: "14px", color: "#757575", fontWeight: 500 }}
                >
                  Yes, It was submitted through old NOC Portal
                </Typography>
              }
              sx={{ mb: 1 }}
            />
            <FormControlLabel
              value="offline"
              control={
                <Radio
                  size="small"
                  sx={{
                    color: "#8BA6C7",
                    "&.Mui-checked": { color: "#3A1C73" },
                  }}
                />
              }
              label={
                <Typography
                  sx={{ fontSize: "14px", color: "#757575", fontWeight: 500 }}
                >
                  No, It was processed Offline
                </Typography>
              }
            />
          </RadioGroup>

          {legacyOption === "online" && (
            <Box sx={{ mt: 3 }}>
              <Box
                className="bg-orange-100"
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <InfoOutlinedIcon
                  sx={{
                    color: "#3A1C73",
                    fontSize: 20,
                    mt: "2px",
                    flexShrink: 0,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#374151",
                    lineHeight: 1.5,
                  }}
                >
                  In cases where the system is unable to fetch details or
                  application details are not available with the applicant shall
                  follow the procedure prescribed for onboarding of offline
                  applications.
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#212121",
                  mb: 1.5,
                }}
              >
                Search Your Application by Application ID *
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  border: `1px solid ${legacySearchValue.trim() ? "#212121" : "#000000"}`,
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1,
                  backgroundColor: "#fff",
                }}
              >
                <SearchIcon
                  sx={{ color: "#9CA3AF", fontSize: 20, flexShrink: 0 }}
                />
                <InputBase
                  fullWidth
                  placeholder="UP/NH/001"
                  value={legacySearchValue}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setLegacySearchValue(nextValue);

                    if (selectedLegacyApplication) {
                      setSelectedLegacyApplication(null);
                    }
                  }}
                  sx={{
                    fontSize: "14px",
                    color: "#000000",
                    "& .MuiInputBase-input::placeholder": {
                      color: legacySearchValue.trim() ? "#c7c9cc" : "#c7c9cc",
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              {showOnlineDropdown && (
                <Box
                  sx={{
                    mt: 0.75,
                    border: "1px solid #D9DDE5",
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                    maxHeight: 180,
                    overflowY: "auto",
                  }}
                >
                  {filteredOnlineResults.map((item) => (
                    <Box
                      key={item.id}
                      onClick={() => {
                        setSelectedLegacyApplication(item);
                        setLegacySearchValue(item.application_code);
                        setFilteredOnlineResults([]);
                      }}
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        borderBottom: "1px solid #EEF1F5",
                        cursor: "pointer",
                        "&:last-of-type": { borderBottom: "none" },
                        "&:hover": { backgroundColor: "#F7F8FB" },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#212121",
                          fontWeight: 600,
                        }}
                      >
                        {item.application_code}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                        Application Type : {item.application_type}
                      </Typography>
                    </Box>
                  ))}
                  {isOnlineSearching && (
                    <Typography
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        fontSize: "13px",
                        color: "#6B7280",
                      }}
                    >
                      Searching...
                    </Typography>
                  )}
                  {!isOnlineSearching && filteredOnlineResults.length === 0 && (
                    <Typography
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        fontSize: "13px",
                        color: "#6B7280",
                      }}
                    >
                      No application found
                    </Typography>
                  )}
                </Box>
              )}
              {selectedLegacyApplication && (
                <Box
                  sx={{
                    mt: 2,
                    border: "1px solid #D9DDE5",
                    borderRadius: "10px",
                    backgroundColor: "#F2F2F6",
                    px: 2,
                    py: 1.75,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 600,
                        color: "#212121",
                        mb: 0.25,
                      }}
                    >
                      {selectedLegacyApplication.application_code}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                      Application Type :{" "}
                      {selectedLegacyApplication.application_type}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedLegacyApplication(null);
                      setLegacySearchValue("");
                      setFilteredOnlineResults([]);
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 24, color: "#222" }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}

          {legacyOption === "offline" && (
            <Box sx={{ mt: 3 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#212121",
                  mb: 1.5,
                }}
              >
                Enter Your Application Number *
              </Typography>
              <Box
                sx={{
                  border: "1px solid #BDBDBD",
                  borderRadius: "6px",
                  px: 2,
                  py: 1.25,
                  mb: 3,
                  backgroundColor: "#fff",
                }}
              >
                <InputBase
                  fullWidth
                  placeholder="Application Number"
                  value={offlineLegacySearchValue}
                  onChange={(e) => setOfflineLegacySearchValue(e.target.value)}
                  sx={{ fontSize: "14px", color: "#374151" }}
                />
              </Box>

              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#212121",
                  mb: 1.5,
                }}
              >
                Select Your Application Type *
              </Typography>
              <RadioGroup
                row
                value={legacyApplicationType}
                onChange={(e) => {
                  const value = e.target.value;

                  if (value === "row" && !hasOrganization) {
                    console.log(hasOrganization, " sjsjjs");
                    setOpenOrganizationAlert(true);
                    return;
                  }

                  setLegacyApplicationType(value);
                }}
                sx={{ columnGap: 2, rowGap: 1, flexWrap: "wrap" }}
              >
                <FormControlLabel
                  value="access"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        color: "#8BA6C7",
                        "&.Mui-checked": { color: "#3A1C73" },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#6B7280",
                          fontWeight: 500,
                        }}
                      >
                        Access Application
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#8B8F97",
                          lineHeight: "18px",
                        }}
                      >
                        (Fuel Stations, Wayside Amenities, Private
                        Residential/Commercial Properties, Road Connections,
                        etc.)
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", mr: 1, maxWidth: "100%" }}
                />
                <FormControlLabel
                  value="row"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        color: "#8BA6C7",
                        "&.Mui-checked": { color: "#3A1C73" },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#6B7280",
                          fontWeight: 500,
                        }}
                      >
                        Right of Way(RoW) Application
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#8B8F97",
                          lineHeight: "18px",
                        }}
                      >
                        (OFC, BharatNet, Telecom, Towers, Pipelines, Power
                        Cables, and other utility infrastructure works, etc.)
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", maxWidth: "100%" }}
                />
              </RadioGroup>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ px: 3, py: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Button
            fullWidth
            variant="contained"
            disabled={
              !legacyOption ||
              (legacyOption === "online" && !selectedLegacyApplication) ||
              (legacyOption === "offline" &&
                (!legacyApplicationType ||
                  !(offlineLegacySearchValue.trim().length > 3)))
            }
            onClick={async () => {
              if (
                legacyOption === "offline" &&
                legacyApplicationType === "row" &&
                !hasOrganization
              ) {
                console.log("sjsjs", hasOrganization);

                setOpenOrganizationAlert(true);
                return;
              }
              if (legacyOption === "online") {
                if (!selectedLegacyApplication) {
                  toast.error(
                    "Please select your application from search results.",
                  );
                  return;
                }

                const moduleType =
                  selectedLegacyApplication.application_type === "Access"
                    ? "access"
                    : "row";

                setDialogOpen(false);
                navigate(
                  `/legacy-application?${moduleType}=${selectedLegacyApplication.id}`,
                );
                return;
              }

              if (offlineLegacySearchValue.length > 6) {
                try {
                  const postCreateOfflineApplication: any = await dispatch(
                    createOfflineApplicationThunk({
                      application_code: offlineLegacySearchValue,
                      application_source: "OFFLINE",
                      application_category:
                        legacyApplicationType === "access" ? 1 : 2,
                    }),
                  ).unwrap();

                  if (postCreateOfflineApplication?.success) {
                    navigate(
                      `/legacy-application?${legacyApplicationType}=${postCreateOfflineApplication?.data?.application_id}`,
                    );
                  } else {
                    toast.warn(
                      postCreateOfflineApplication?.message ||
                        "Unable to process this application.",
                    );
                  }
                } catch (error: any) {
                  const errorMessage =
                    error?.message ||
                    error?.response?.data?.message ||
                    "Unable to process this application.";

                  toast.error(errorMessage);
                }
              } else {
                toast.error(
                  "Please enter a valid application number with more than 6 characters for offline applications.",
                  {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                  },
                );
              }
            }}
            sx={{
              borderRadius: "999px",
              textTransform: "none",
              height: "35px",
              fontSize: "15px",
              py: 1.25,
              backgroundColor: "#3A1C73",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#3A1C73" },
              "&.Mui-disabled": {
                backgroundColor: "#E5E5E5",
                color: "#9CA3AF",
              },
            }}
          >
            Continue
          </Button>
        </Box>
      </Drawer>
      <AlertDialogBox
        open={openOrganizationAlert}
        onClose={() => setOpenOrganizationAlert(false)}
      />
    </>
  );
};

export default LegacyDrawer;
